import Vue from 'vue'
import Router from 'vue-router'
import routesAuthorized from '@/router/routes'

const route = (path: string, view: string, name: string, meta: any) => ({
  name: name || view,
  component: (resolve: any) => import(`@/views/${view}.vue`).then(resolve),
  path,
  meta,
})

function selectRouters() {
  const r = routesAuthorized.map(path => route(path.path, path.view, path.name, path.meta))
  return r
}

Vue.use(Router)

const router = new Router({
  mode: 'history',
  routes: selectRouters(),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }

    if (to.hash) {
      return { selector: to.hash }
    }

    return { x: 0, y: 0 }
  },
})

router.beforeEach((to, from, next) => {
  const allowedRoutes: string[] = [
    'inicio',
    'cadastroMedico',
    'assinatura',
    'TokenExchange',
    'BackofficeContratos',
    'BackofficeContratosDetails',
    'FinanceiroUploadNotaFiscal',
    'termos-de-uso',
    'politica-de-privacidade',
    'NoDefaultRoute',
    'NoContent',
    // Rotas de logout: precisam passar pelo guard mesmo com o token local expirado.
    // Sem isso o guard cai em next('/inicio') e o Keycloak nunca é chamado — a sessão
    // SSO continua ativa e o próximo "Acessar" reautentica em silêncio.
    'sair',
    'logout',
  ]

  // LER DYNAMICAMENTE do localStorage ao invés de usar valores em cache
  // Isso garante que sempre usamos os dados mais recentes (importante para impersonação)
  const keycloakUrl = (window as any).KEYCLOAK_AUTHORITY_URL || (window as any).API_FINX_AUTH
  const realm = (window as any).KEYCLOAK_REALM || 'finx'
  const clientId = (window as any).KEYCLOAK_CLIENT_ID || 'finx-front'
  const storageKey1 = `oidc.user:${keycloakUrl}/realms/${realm}:${clientId}`
  const storageKey2 = `oidc.user:${(window as any).API_FINX_AUTH}:portal-client-2`

  const AUTH_TOKEN = localStorage.getItem(storageKey2) || localStorage.getItem(storageKey1)
  let PORTAL_CLIENT: any = {}
  let isTokenExpired = false

  try {
    if (AUTH_TOKEN) {
      PORTAL_CLIENT = JSON.parse(AUTH_TOKEN)
      const expiresAt = Number(PORTAL_CLIENT.expires_at || PORTAL_CLIENT.expiresAt)
      if (expiresAt) {
        const now = Math.floor(Date.now() / 1000)
        isTokenExpired = expiresAt < now
      }
    }
  } catch (e) {
    console.error('Erro ao fazer parse do AUTH_TOKEN:', e)
  }

  if (AUTH_TOKEN && !isTokenExpired) {
    // Permitir acesso a rotas públicas sem verificação de permissões
    if (allowedRoutes.includes((to as any).name)) {
      next()
      return
    }
    // Verificar permissões (funciona tanto para login normal quanto para impersonação)
    // Durante impersonação, PORTAL_CLIENT já contém os dados do usuário personificado
    // que foram buscados do backend via /usuarios/roles
    const routeMeta = (to as any).meta || {}
    const { isAuthorized = false, keyAuthorized } = routeMeta
    const userPageAuthorized = PORTAL_CLIENT.profile && PORTAL_CLIENT.profile.pageAuthorized

    // Durante personificação, sempre permitir acesso à tela de personificação
    // para que o operador possa visualizar a sessão ativa e sair dela
    const isImpersonating = sessionStorage.getItem('IMPERSONATION_ACTIVE')
    if (isImpersonating === 'true' && keyAuthorized === 'suporte-personificar') {
      next()
      return
    }

    const hasPermission = isAuthorized
                         || (userPageAuthorized && keyAuthorized && userPageAuthorized.includes(keyAuthorized))

    if (hasPermission) {
      const postLoginUrl = localStorage.getItem('postLoginUrl')
      if (postLoginUrl) {
        localStorage.removeItem('postLoginUrl')
        window.location.href = postLoginUrl
      } else {
        next()
      }
    } else {
      // Durante impersonação, redirecionar para /perfil (sempre acessível) em vez de
      // /inicio, para evitar loop: /inicio tenta redirecionar e cai de volta aqui
      const isImpersonating = sessionStorage.getItem('IMPERSONATION_ACTIVE')
      if (isImpersonating === 'true') {
        next('/perfil')
      } else {
        next('/inicio')
      }
    }
  } else if (!allowedRoutes.includes((to as any).name)) {
    const fullPath = (to as any).fullPath
    if (fullPath && fullPath !== '/' && fullPath !== '/inicio') {
      localStorage.setItem('postLoginUrl', fullPath)
    }
    next('/inicio')
  } else {
    next()
  }
})

export default router
